import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import { SOCIAL_MEDIA_CONFIG } from '../../config/socialMediaConfig';
import './FacebookCallback.css';

interface CallbackState {
  storeId: string;
  storeName: string;
  timestamp: number;
}

export const FacebookCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getIdToken, isAuthenticated } = useAuth();

  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing Facebook connection...');
  const [connectedPages, setConnectedPages] = useState<any[]>([]);
  const [hasStarted, setHasStarted] = useState(false);

  useEffect(() => {
    // Wait for Cognito to restore session after redirect before processing
    if (isAuthenticated && !hasStarted) {
      setHasStarted(true);
      processFacebookCallback();
    }
  }, [isAuthenticated, hasStarted]);

  const processFacebookCallback = async () => {
    try {
      // Get authorization code and state from URL parameters
      const authCode = searchParams.get('code');
      const stateParam = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorCode = searchParams.get('error_code');
      const errorMessage = searchParams.get('error_message');
      const errorDescription = searchParams.get('error_description');

      // Handle Facebook OAuth errors
      if (errorParam || errorCode || errorMessage) {
        const fullErrorMessage = [
          errorParam && `Error: ${errorParam}`,
          errorCode && `Code: ${errorCode}`,
          errorMessage && `Message: ${decodeURIComponent(errorMessage)}`,
          errorDescription && `Description: ${decodeURIComponent(errorDescription)}`
        ].filter(Boolean).join(' | ');

        console.error('Facebook OAuth Error Details:', {
          error: errorParam,
          error_code: errorCode, 
          error_message: errorMessage,
          error_description: errorDescription,
          fullUrl: window.location.href
        });

        // Special handling for domain configuration error
        if (errorCode === '1349048') {
          throw new Error(
            'Facebook App Domain Configuration Error: The current domain is not configured in your Facebook App settings. ' +
            'Please add the current domain to your Facebook App\'s "App Domains" and "Valid OAuth Redirect URIs" settings. ' +
            `Current domain: ${window.location.origin}`
          );
        }

        throw new Error(`Facebook OAuth failed: ${fullErrorMessage}`);
      }

      if (!authCode || !stateParam) {
        console.error('Missing OAuth parameters:', {
          hasCode: !!authCode,
          hasState: !!stateParam,
          allParams: Object.fromEntries(searchParams.entries()),
          fullUrl: window.location.href
        });
        throw new Error('Missing authorization code or state parameter from Facebook OAuth response');
      }

      // Decode and validate state
      const decodedState = JSON.parse(atob(stateParam)) as CallbackState;
      const storedState = localStorage.getItem('facebook_oauth_state');

      if (!storedState) {
        throw new Error('Invalid OAuth state - no stored state found');
      }

      // Compare storeId and storeName only (timestamp differs between localStorage and URL state)
      const storedParsed = JSON.parse(storedState);
      if (decodedState.storeId !== storedParsed.storeId || decodedState.storeName !== storedParsed.storeName) {
        throw new Error('Invalid OAuth state - possible security issue');
      }

      // Clear stored state
      localStorage.removeItem('facebook_oauth_state');

      setMessage('Exchanging authorization code for access token...');

      // Exchange authorization code for access token
      const tokenResponse = await fetch(API_ENDPOINTS.facebookOAuthExchangeCode(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await getIdToken()}`
        },
        body: JSON.stringify({
          code: authCode,
          redirectUri: SOCIAL_MEDIA_CONFIG.facebook.redirectUri,
          storeId: decodedState.storeId
        })
      });

      if (!tokenResponse.ok) {
        throw new Error('Failed to exchange authorization code');
      }

      const tokenData = await tokenResponse.json();
      
      if (!tokenData.success) {
        throw new Error(tokenData.error || 'Token exchange failed');
      }

      setMessage('Retrieving your Facebook pages...');

      // Get user's Facebook pages
      const pagesResponse = await fetch(
        `${API_ENDPOINTS.facebookPages()}?accessToken=${tokenData.data.access_token}`,
        {
          headers: {
            'Authorization': `Bearer ${await getIdToken()}`
          }
        }
      );

      if (!pagesResponse.ok) {
        throw new Error('Failed to retrieve Facebook pages');
      }

      const pagesData = await pagesResponse.json();
      
      if (!pagesData.success || !pagesData.data?.data) {
        throw new Error('No Facebook pages found');
      }

      setMessage('Connecting your Facebook pages to BigSystemOne...');

      // Connect each page to the store
      const connectedPagesList: any[] = [];
      
      for (const page of pagesData.data.data) {
        try {
          const connectResponse = await fetch(API_ENDPOINTS.socialMediaAccounts(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${await getIdToken()}`
            },
            body: JSON.stringify({
              storeId: decodedState.storeId,
              platform: 'facebook',
              accountName: page.name,
              pageId: page.id,
              accessToken: page.access_token,
              platformAccountId: page.id,
              profileImageUrl: page.picture?.data?.url || '',
              followerCount: page.fan_count || 0,
              category: page.category,
              isActive: true,
              platformData: {
                tasks: page.tasks || [],
                category: page.category,
                fanCount: page.fan_count
              }
            })
          });

          if (connectResponse.ok) {
            const result = await connectResponse.json();
            connectedPagesList.push({
              name: page.name,
              category: page.category,
              fanCount: page.fan_count,
              accountId: result.data?.accountId
            });
          } else {
            console.warn(`Failed to connect page: ${page.name}`);
          }
        } catch (error) {
          console.warn(`Error connecting page ${page.name}:`, error);
        }
      }

      if (connectedPagesList.length === 0) {
        throw new Error('Failed to connect any Facebook pages');
      }

      setConnectedPages(connectedPagesList);
      setStatus('success');
      setMessage(`Successfully connected ${connectedPagesList.length} Facebook page${connectedPagesList.length > 1 ? 's' : ''}!`);

      // Auto-redirect after 3 seconds
      setTimeout(() => {
        navigate('/social-media');
      }, 3000);

    } catch (error) {
      console.error('Facebook OAuth callback error:', error);
      setStatus('error');
      setMessage(error instanceof Error ? error.message : 'An unexpected error occurred');
      
      // Auto-redirect to social media page after 5 seconds on error
      setTimeout(() => {
        navigate('/social-media');
      }, 5000);
    }
  };

  const handleManualRedirect = () => {
    navigate('/social-media');
  };

  return (
    <div className="facebook-callback">
      <div className="callback-container">
        <div className="callback-header">
          <div className="platform-icon">FB</div>
          <h1>Facebook Integration</h1>
        </div>

        <div className={`callback-content ${status}`}>
          {status === 'processing' && (
            <div className="processing-state">
              <div className="spinner-container">
                <div className="spinner"></div>
              </div>
              <h2>Connecting to Facebook</h2>
              <p>{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="success-state">
              <div className="success-icon">[SUCCESS]</div>
              <h2>Connection Successful!</h2>
              <p>{message}</p>
              
              {connectedPages.length > 0 && (
                <div className="connected-pages">
                  <h3>Connected Pages:</h3>
                  <ul>
                    {connectedPages.map((page, index) => (
                      <li key={index} className="page-item">
                        <div className="page-info">
                          <strong>{page.name}</strong>
                          {page.category && <span className="page-category">{page.category}</span>}
                          {page.fanCount !== undefined && (
                            <span className="page-fans">{page.fanCount} followers</span>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <p className="redirect-note">Redirecting to Social Media Dashboard...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="error-state">
              <div className="error-icon">[ERROR]</div>
              <h2>Connection Failed</h2>
              <p>{message}</p>
              
              {message.includes('Domain Configuration Error') && (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  backgroundColor: '#fff3cd', 
                  border: '1px solid #ffeaa7',
                  borderRadius: '8px',
                  textAlign: 'left'
                }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>[CONFIG] Facebook App Configuration Required</h4>
                  <p style={{ fontSize: '0.9rem', margin: '0 0 1rem 0', color: '#856404' }}>
                    Add these settings to your Facebook App (ID: 1749465492375238):
                  </p>
                  <div style={{ fontSize: '0.85rem', color: '#856404' }}>
                    <strong>App Domains:</strong>
                    <code style={{ display: 'block', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', margin: '0.25rem 0' }}>
                      {window.location.hostname}
                    </code>
                    <strong>Valid OAuth Redirect URIs:</strong>
                    <code style={{ display: 'block', padding: '0.5rem', backgroundColor: '#fff', borderRadius: '4px', margin: '0.25rem 0' }}>
                      {window.location.origin}/social-media/facebook-callback
                    </code>
                  </div>
                </div>
              )}
              
              <div className="error-actions">
                <button onClick={handleManualRedirect} className="retry-btn">
                  Return to Social Media Dashboard
                </button>
              </div>
              
              <p className="redirect-note">Auto-redirecting in a few seconds...</p>
            </div>
          )}
        </div>

        <div className="callback-footer">
          <div className="security-note">
            <span className="shield-icon">[SECURE]</span>
            <p>Your Facebook connection is secure and encrypted</p>
          </div>
        </div>
      </div>
    </div>
  );
};